import { useEffect } from 'react';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

// utils
import { bgBlur } from '../../../utils/cssStyles';
// components
import Iconify from '../../../components/iconify';
import Toast from '../../../components/toast';
//
import AccountPopover from './AccountPopover';
import { clearError } from '../../../slices/error';
import type { RootState } from '../../../store';

// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const HEADER_MOBILE = 64;

const HEADER_DESKTOP = 92;

const StyledRoot = styled(AppBar)(({ theme }) => ({
	...bgBlur({ color: theme.palette.background.default }),
	boxShadow: 'none',
	[theme.breakpoints.up('lg')]: {
		width: `calc(100% - ${NAV_WIDTH + 1}px)`,
	},
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
	minHeight: HEADER_MOBILE,
	[theme.breakpoints.up('lg')]: {
		minHeight: HEADER_DESKTOP,
		padding: theme.spacing(0, 5),
	},
}));

// ----------------------------------------------------------------------

interface HeaderProps {
	onOpenNav: () => void;
}

export default function Header({ onOpenNav }: HeaderProps) {
	const globalError = useSelector((state: RootState) => state.error?.error?.message);
	const dispatch = useDispatch();
	const { pathname } = useLocation();

	const handleSnackbarClose = () => {
		if (globalError) {
			dispatch(clearError());
		}
	};

	// clear error state when user navigates away from current route
	useEffect(() => {
		if (pathname) {
			// work around the warning that pathname is not used in the effect
		}
		dispatch(clearError());
	}, [pathname, dispatch]);

	return (
		<StyledRoot>
			<StyledToolbar>
				<IconButton
					onClick={onOpenNav}
					sx={{
						mr: 1,
						color: 'text.primary',
						display: { lg: 'none' },
					}}
				>
					<Iconify icon="eva:menu-2-fill" />
				</IconButton>

				{globalError && <Toast onClose={handleSnackbarClose} title="Error" body={globalError} severity="error" />}

				<Box sx={{ flexGrow: 1 }} />

				<Stack
					direction="row"
					alignItems="center"
					spacing={{
						xs: 0.5,
						sm: 1,
					}}
				>
					<AccountPopover />
				</Stack>
			</StyledToolbar>
		</StyledRoot>
	);
}
